import type { SenderCountrySelection } from 'Modals/CreateReturnLabelModal/CreateReturnLabel.types'

import {
  createSelectOption,
  getFullContactName,
  parseStreetString,
} from 'Utilities'

import type { getReturnCase_returnCaseItem_employee } from 'Constants/graphql/queries/detail/__generated__/getReturnCase'
import type { getEmployee_employee } from 'Constants/graphql/queries/employee/__generated__/getEmployee'
import type { getOrganisation_organisation } from 'Constants/graphql/queries/organisation/__generated__/getOrganisation'

import {
  PortalReturnLabelAddressSourceEnum,
  type PortalReturnLabelModalFormSchema,
} from './PortalReturnLabelModal.types'

type CountryList = Record<string, string>

interface AddressLike {
  city: string | null
  country: string | null
  name?: string | null
  street: string | null
  zipcode: string | null
}

// Duck-typed so the modal can pass either an active admin organisation or the
// employee's organisation as the office-address fallback source.
interface OfficeFallbackOrganisation {
  contact: { address: AddressLike | null } | null
  fullName: string
}

const emptyAddressFormValues: Omit<
  PortalReturnLabelModalFormSchema,
  'addressSource'
> = {
  addedReturnCases: [],
  city: '',
  country: null,
  email: '',
  fullName: '',
  phoneNumber: '',
  street: '',
  streetNumber: '',
  zipcode: '',
}

const buildAddressFormValues = (
  addressSource: PortalReturnLabelAddressSourceEnum,
  name: string,
  address: AddressLike,
  countryList: CountryList,
): PortalReturnLabelModalFormSchema => {
  const { streetName, streetNumber } = parseStreetString(address.street || '')
  const countryCode = address.country || ''

  return {
    ...emptyAddressFormValues,
    addressSource,
    city: address.city || '',
    country: countryCode
      ? createSelectOption({
          label: countryList[countryCode] || countryCode,
          value: countryCode,
        })
      : null,
    fullName: name,
    street: streetName || '',
    streetNumber: streetNumber || '',
    zipcode: address.zipcode || '',
  }
}

export const getPortalReturnLabelOfficeAddress = (
  returnCaseEmployee: getReturnCase_returnCaseItem_employee | null,
  organisation: OfficeFallbackOrganisation | null,
  countryList: CountryList,
): PortalReturnLabelModalFormSchema => {
  const office = returnCaseEmployee?.office
  const officeAddress = office?.address

  if (officeAddress) {
    return buildAddressFormValues(
      PortalReturnLabelAddressSourceEnum.OFFICE,
      officeAddress.name || office.locationName || '',
      officeAddress,
      countryList,
    )
  }

  const organisationAddress = organisation?.contact?.address

  if (organisationAddress) {
    return buildAddressFormValues(
      PortalReturnLabelAddressSourceEnum.OFFICE,
      organisationAddress.name || organisation.fullName || '',
      organisationAddress,
      countryList,
    )
  }

  return {
    ...emptyAddressFormValues,
    addressSource: PortalReturnLabelAddressSourceEnum.OFFICE,
  }
}

export const getPortalReturnLabelCustomAddress = (
  activeEmployee: getEmployee_employee | null,
  activeOrganisation: getOrganisation_organisation | null,
  senderCountry: SenderCountrySelection | null,
): PortalReturnLabelModalFormSchema => {
  const isOrganisationContext = Boolean(activeOrganisation?.id)

  if (isOrganisationContext || !activeEmployee?.contact) {
    return {
      ...emptyAddressFormValues,
      addressSource: PortalReturnLabelAddressSourceEnum.CUSTOM,
      country: senderCountry,
    }
  }

  const { contact } = activeEmployee

  if (!contact.address) {
    return {
      ...emptyAddressFormValues,
      addressSource: PortalReturnLabelAddressSourceEnum.CUSTOM,
      country: senderCountry,
      fullName: getFullContactName(contact),
    }
  }

  const { streetName, streetNumber } = parseStreetString(
    contact.address.street || '',
  )

  return {
    addedReturnCases: [],
    addressSource: PortalReturnLabelAddressSourceEnum.CUSTOM,
    city: contact.address.city || '',
    country: senderCountry,
    fullName: contact.address.name || getFullContactName(contact),
    street: streetName || '',
    streetNumber: streetNumber || '',
    zipcode: contact.address.zipcode || '',
  }
}
